import { useState, useEffect, useRef } from 'react';
import { MessageSquare, ChevronRight, ChevronLeft, Send, Bot, User, Sparkles } from 'lucide-react';
import { getBackendUrl } from '../utils/api';

const AIChatbot = ({ user, isAuthenticated }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  
  // Check if user has active subscription
  // Admins always have access
  const hasActiveSubscription = user?.role === 'admin' || 
                                  (user?.subscription_status === 'active' && 
                                   user?.subscription_days_remaining > 0);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    
    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      // Get auth token from storage
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const backendUrl = getBackendUrl();
      
      const response = await fetch(`${backendUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle specific error cases
        if (response.status === 401 || response.status === 403) {
          // Token expired or unauthorized
          setMessages(prev => [...prev, { 
            role: 'assistant', 
            content: '🔐 Η συνεδρία σου έληξε. Παρακαλώ κάνε logout και login ξανά για να συνεχίσεις.' 
          }]);
          return;
        }
        
        if (response.status === 402) {
          // Payment required
          setMessages(prev => [...prev, { 
            role: 'assistant', 
            content: '💳 ' + (errorData.detail || 'Χρειάζεται ενεργή συνδρομή για χρήση του βοηθού.') 
          }]);
          return;
        }
        
        throw new Error(errorData.detail || 'Failed to get response');
      }

      const data = await response.json();
      
      // Add response to chat
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Λυπάμαι, κάτι πήγε στραβά. Παρακαλώ δοκίμασε ξανά ή κάνε refresh τη σελίδα.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Toggle Button - Bottom right on mobile, middle right on desktop */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 z-50 flex items-center gap-2 ${
          isOpen 
            ? 'right-[400px] top-1/2 -translate-y-1/2' 
            : 'right-0 bottom-20 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2'
        }`}
        style={{
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
          padding: isOpen ? '16px 12px' : '10px 8px',
          writingMode: isOpen ? 'horizontal-tb' : 'vertical-rl',
        }}
        aria-label={isOpen ? "Close Content Assistant" : "Open Content Assistant"}
      >
        {isOpen ? (
          <ChevronRight className="w-5 h-5" />
        ) : (
          <>
            <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="font-semibold text-xs sm:text-sm whitespace-nowrap">Βοηθός</span>
            <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
          </>
        )}
      </button>

      {/* Chat Sidebar - Responsive width */}
      <div
        className={`fixed top-0 right-0 h-full bg-white dark:bg-gray-800 shadow-2xl transition-transform duration-300 ease-in-out z-40 w-full sm:w-[400px] ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } ${!isOpen ? 'invisible' : 'visible'}`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 flex items-center gap-3">
            <div className="bg-white p-2 rounded-full flex items-center justify-center" style={{ width: '48px', height: '48px' }}>
              <img 
                src="/radio-logo.png" 
                alt="Radio Solutions" 
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg">Βοηθός Περιεχομένου</h3>
              <p className="text-xs text-blue-100">Ρώτα με οτιδήποτε!</p>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-xs">Online</span>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900">
            {/* Subscription Check - Show message for non-paid users */}
            {!hasActiveSubscription && (
              <div className="text-center text-gray-700 dark:text-gray-200 mt-8 p-6 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30 rounded-xl shadow-md border-2 border-purple-200 dark:border-purple-700">
                <div className="bg-white rounded-full w-20 h-20 mx-auto mb-4 p-3 shadow-lg">
                  <img 
                    src="/radio-logo.png" 
                    alt="Radio Solutions" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="mb-4">
                  <span className="text-4xl">🔒</span>
                </div>
                <p className="text-lg font-bold text-purple-700 mb-2">Βοηθός Περιεχομένου</p>
                <p className="text-sm text-gray-600 mb-4 px-2">
                  {user?.subscription_status === 'expired' 
                    ? 'Η συνδρομή σου έχει λήξει. Ανανέωσέ την για να συνεχίσεις να χρησιμοποιείς τον βοηθό! 🎙️✨'
                    : 'Παρακαλώ ολοκλήρωσε την πληρωμή για να δημιουργήσουμε μαζί εξαιρετικό περιεχόμενο για την εκπομπή σου! 🎙️✨'
                  }
                </p>
                <button
                  onClick={() => window.location.href = user?.subscription_status === 'expired' ? '/' : '/signup'}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition-all"
                >
                  {user?.subscription_status === 'expired' ? 'Ανανέωση Συνδρομής' : 'Ενεργοποίηση Συνδρομής'}
                </button>
              </div>
            )}
            
            {/* Regular chat messages - Only for paid users */}
            {hasActiveSubscription && messages.length === 0 && (
              <div className="text-center text-gray-500 mt-8">
                <div className="bg-white rounded-full w-20 h-20 mx-auto mb-4 p-3 shadow-md">
                  <img 
                    src="/radio-logo.png" 
                    alt="Radio Solutions" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="text-lg font-medium text-gray-700 dark:text-gray-200">Γεια σου! 👋</p>
                <p className="text-sm mt-2 px-4">Είμαι ο Βοηθός Περιεχομένου και είμαι εδώ για να σε βοηθήσω!</p>
                <div className="mt-4 text-xs text-gray-400 space-y-1">
                  <p>💬 Ρώτα με οτιδήποτε</p>
                  <p>🌍 Ελληνικά & English</p>
                  <p>⚡ Γρήγορες απαντήσεις</p>
                </div>
              </div>
            )}
            
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                )}
                
                <div
                  className={`max-w-[75%] p-3 rounded-lg ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-tr-none'
                      : 'bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 rounded-tl-none shadow-sm'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                </div>
                
                {msg.role === 'user' && (
                  <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-gray-400 to-gray-500 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                )}
              </div>
            ))}
            
            {isLoading && (
              <div className="flex gap-3 justify-start">
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="bg-white border border-gray-200 p-3 rounded-lg shadow-sm">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="flex flex-col gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Γράψε το μήνυμά σου..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 resize-none"
                rows="2"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
              >
                <Send className="w-4 h-4" />
                <span>Αποστολή</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay when sidebar is open */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-30"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default AIChatbot;
